import { ChevronDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FeatureBadge from 'components/NEW/featureBadge';
import { getMenuFeatureType, preferredFeatureType } from 'utils/featureHighlights';
import newMenuBadges from 'config/newMenuBadges.json';


const SidebarSection = ({ title, icon, items, isActive, isParentActive, isCollapsed }) => {
    const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
    const [currentTime, setCurrentTime] = useState(() => Date.now());
    const badgeType = item => getMenuFeatureType(item.location, deployEnv, currentTime);
    const parentBadgeType = preferredFeatureType(items.map(badgeType));
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Automatically open the dropdown if any item in this section is active
        if (isParentActive) {
            setIsOpen(true);
        }
    }, [isParentActive]);

    useEffect(() => {
        const nextExpiry = newMenuBadges
            .map((badge) => Date.parse(badge.expiresAt))
            .filter((expiry) => expiry > currentTime)
            .sort((left, right) => left - right)[0];
        if (!nextExpiry) return undefined;

        const timeout = window.setTimeout(
            () => setCurrentTime(Date.now()),
            Math.min(2147483647, Math.max(0, nextExpiry - Date.now() + 1))
        );
        return () => window.clearTimeout(timeout);
    }, [currentTime]);

    const toggleDropdown = () => setIsOpen(!isOpen);

    return (
        <div className="relative flex flex-col">
            <button
                type="button"
                aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title}`}
                aria-expanded={isOpen}
                className="flex w-full items-center p-4 text-left cursor-pointer hover:bg-gray-100"
                onClick={toggleDropdown}
            >
                {/* Display only the parent icon when collapsed */}
                <img
                    src={isParentActive ? icon[0] : icon[1]}
                    alt={title}
                    className="w-6 h-6 mr-2"
                />

                {/* Only show title when sidebar is expanded */}
                {!isCollapsed && (
                    <span className={isParentActive ? 'text-brandRed' : 'text-black'}>
                        {title}
                        <FeatureBadge type={parentBadgeType} />
                    </span>
                )}

                {/* Conditionally render the arrow only if sidebar is not collapsed */}
                {!isCollapsed && (
                    <ChevronDown
                        size={24}
                        aria-hidden="true"
                        className={`ml-auto shrink-0 transition-transform duration-300 ${isOpen ? '-rotate-180' : ''}`}
                    />
                )}
            </button>

            {/* Show child items next to parent icon in collapsed state */}
            {isCollapsed && isOpen && (
                <div className="absolute left-full top-0 ml-4 flex flex-col bg-white shadow-lg z-10 w-40">
                    {items.map((item, index) => (
                        <Link
                            key={index}
                            to={item.location}
                            className={`p-4 hover:bg-gray-100 cursor-pointer ${isActive(item.name) ? 'text-brandRed' : 'text-black'}`}
                        >
                            {item.name}
                            <FeatureBadge type={badgeType(item)} />
                        </Link>
                    ))}
                </div>
            )}

            {/* Expanded dropdown for child items when the sidebar is not collapsed */}
            {!isCollapsed && isOpen && (
                <div className="flex flex-col ml-8">
                    {items.map((item, index) => (
                        <Link
                            key={index}
                            to={item.location}
                            className={`p-4 hover:bg-gray-100 cursor-pointer ${isActive(item.name) ? 'text-brandRed' : 'text-black'}`}
                        >
                            {item.name}
                            <FeatureBadge type={badgeType(item)} />
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(SidebarSection);
